/**
 * ParkingZoneFormDialog Component
 *
 * Dialog for creating/editing parking zones.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { describeApiError } from '@/lib/describeApiError';
import {
  DialogContent,
  TextField,
  Stack,
  MenuItem,
} from '@mui/material';
import { FormDialog } from '@/components/common/FormDialog';
import {
  useCreateParkingZone,
  useUpdateParkingZone,
  type ParkingZone,
  type ZoneType,
} from '@/hooks/useParking';
import { useNotify } from '@/stores/notificationStore';
import { NumberField } from '@/components/common/NumberField';

interface ParkingZoneFormDialogProps {
  open: boolean;
  zone?: ParkingZone | null;
  onClose: () => void;
}

interface FormData {
  name: string;
  zoneType: ZoneType;
  sortOrder: number;
  notes: string;
}

const initialFormData: FormData = {
  name: '',
  zoneType: 'outdoor',
  sortOrder: 0,
  notes: '',
};

export function ParkingZoneFormDialog({ open, zone, onClose }: ParkingZoneFormDialogProps) {
  const { t } = useTranslation('equipment');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const isEditing = !!zone;

  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [isDirty, setIsDirty] = useState(false);

  const createMutation = useCreateParkingZone();
  const updateMutation = useUpdateParkingZone();

  useEffect(() => {
    if (zone) {
      setFormData({
        name: zone.name,
        zoneType: zone.zoneType,
        sortOrder: zone.sortOrder,
        notes: zone.notes ?? '',
      });
    } else {
      setFormData(initialFormData);
    }
    setIsDirty(false);
  }, [zone, open]);

  const handleSubmit = async () => {
    try {
      if (isEditing && zone) {
        await updateMutation.mutateAsync({
          id: zone.id,
          data: {
            name: formData.name.trim(),
            zoneType: formData.zoneType,
            sortOrder: formData.sortOrder,
            notes: formData.notes.trim() || null,
          },
        });
        notify.success(t('parking.zoneUpdated'), t('parking.zoneUpdatedDesc', { name: formData.name }));
      } else {
        await createMutation.mutateAsync({
          name: formData.name.trim(),
          zoneType: formData.zoneType,
          sortOrder: formData.sortOrder,
          notes: formData.notes.trim() || null,
        });
        notify.success(t('parking.zoneCreated'), t('parking.zoneCreatedDesc', { name: formData.name }));
      }
      onClose();
    } catch (error) {
      notify.error(tc('error'), describeApiError(error, tc, { fallback: t('parking.saveFailed') }));
    }
  };

  const handleChange = (field: keyof FormData, value: string | number) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setIsDirty(true);
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title={isEditing ? t('parking.editZone') : t('parking.addZone')}
      isDirty={isDirty}
      onSubmit={handleSubmit}
      isLoading={isPending}
      submitLabel={isEditing ? tc('save') : tc('add')}
      maxWidth="sm"
    >
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <TextField
            label={t('parking.zoneName')}
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder={t('parking.zoneNamePlaceholder')}
            fullWidth
            required
          />

          <TextField
            label={t('parking.zoneType')}
            select
            value={formData.zoneType}
            onChange={(e) => handleChange('zoneType', e.target.value)}
            fullWidth
          >
            <MenuItem value="outdoor">{t('parking.zoneTypeOutdoor')}</MenuItem>
            <MenuItem value="indoor">{t('parking.zoneTypeIndoor')}</MenuItem>
            <MenuItem value="covered">{t('parking.zoneTypeCovered')}</MenuItem>
          </TextField>

          <NumberField
            label={t('parking.sortOrder')}
            value={formData.sortOrder}
            onChange={(val) => handleChange('sortOrder', val ?? 0)}
            fullWidth
          />

          <TextField
            label={t('parking.spots')}
            value={formData.notes}
            onChange={(e) => handleChange('notes', e.target.value)}
            multiline
            rows={2}
            fullWidth
          />
        </Stack>
      </DialogContent>
    </FormDialog>
  );
}
